import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Building, ChevronDown, X } from 'lucide-react';
import type { Customer } from '../types/moderator.types';

export interface CustomerDropdownProps {
  customers: Customer[];
  selectedCustomer: Customer | null;
  onSelectCustomer: (customer: Customer | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  showOnlyActive?: boolean;
  className?: string;
}

function matchesCustomerQuery(customer: Customer, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return (
    customer.name.toLowerCase().includes(normalized) ||
    (customer.description?.toLowerCase().includes(normalized) ?? false)
  );
}

export const CustomerDropdown: React.FC<CustomerDropdownProps> = ({
  customers,
  selectedCustomer,
  onSelectCustomer,
  label = 'Choose Customer Organization',
  placeholder = 'Select or type to filter customers...',
  helperText,
  showOnlyActive = true,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // When closed, always reflect the parent selection so the input cannot drift out of sync
  // with helper text / table filters (stale blur handlers previously caused one-step lag).
  const displayValue = isOpen ? query : (selectedCustomer?.name ?? '');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const eligibleCustomers = useMemo(
    () => (showOnlyActive ? customers.filter((c) => c.isActive) : customers),
    [customers, showOnlyActive]
  );

  const filteredCustomers = useMemo(
    () => eligibleCustomers.filter((customer) => matchesCustomerQuery(customer, query)),
    [eligibleCustomers, query]
  );

  const handleInputChange = (value: string) => {
    setQuery(value);
    setIsOpen(true);

    if (!value.trim()) {
      onSelectCustomer(null);
      return;
    }

    const exactMatch = eligibleCustomers.find(
      (customer) => customer.name.toLowerCase() === value.trim().toLowerCase()
    );
    if (exactMatch) {
      onSelectCustomer(exactMatch);
    }
  };

  const handleSelect = (customer: Customer) => {
    onSelectCustomer(customer);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    onSelectCustomer(null);
    setQuery('');
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      if (containerRef.current?.contains(document.activeElement)) {
        return;
      }
      setIsOpen(false);
    }, 0);
  };

  return (
    <div
      className={`rounded-xl shadow-sm border p-6 ${className}`}
      style={{
        backgroundColor: 'var(--color-bg-primary)',
        borderColor: 'var(--color-border)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div className="relative" ref={containerRef}>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
          <Building className="w-4 h-4 inline mr-2" />
          {label}
        </label>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={displayValue}
            placeholder={placeholder}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => {
              setQuery(selectedCustomer?.name ?? '');
              setIsOpen(true);
              inputRef.current?.select();
            }}
            onBlur={handleBlur}
            className="w-full px-4 py-3 pr-20 border rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border)',
              color: displayValue ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
            }}
            aria-expanded={isOpen}
            aria-autocomplete="list"
            role="combobox"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {displayValue && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleClear}
                className="p-1 rounded hover:opacity-80"
                style={{ color: 'var(--color-text-muted)' }}
                aria-label="Clear customer filter"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setIsOpen((open) => !open);
                inputRef.current?.focus();
              }}
              className="p-1 rounded hover:opacity-80"
              style={{ color: 'var(--color-text-muted)' }}
              aria-label="Toggle customer list"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {isOpen && (
            <div
              className="absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto dark-scrollbar"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
                boxShadow: 'var(--shadow-lg)',
              }}
              role="listbox"
            >
              {filteredCustomers.length === 0 ? (
                <p className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  No customers match &ldquo;{query.trim()}&rdquo;
                </p>
              ) : (
                filteredCustomers.map((customer) => (
                  <button
                    key={customer.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(customer)}
                    className="w-full px-4 py-3 text-left hover:bg-opacity-50 transition-colors border-b last:border-b-0"
                    style={{
                      backgroundColor:
                        selectedCustomer?.id === customer.id
                          ? 'var(--color-bg-secondary)'
                          : 'var(--color-bg-primary)',
                      color: 'var(--color-text-primary)',
                      borderColor: 'var(--color-border)',
                    }}
                    role="option"
                    aria-selected={selectedCustomer?.id === customer.id}
                  >
                    <div className="font-medium">{customer.name}</div>
                    {customer.description && (
                      <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {customer.description}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        {helperText && (
          <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>
            {helperText}
          </p>
        )}
      </div>
    </div>
  );
};
